'use client'

import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdEdit,
    MdDelete,
    MdTableChart,
    MdPlaylistAdd,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import ConfirmModal from '@/components/confirm-modal'
import {
    getPayrollTables,
    createPayrollTable,
    updatePayrollTable,
    deletePayrollTable,
    seedPayrollTables,
    HrPayrollTable,
} from '@/lib/api/hr/hr-payroll-table-api'

const TYPE_LABELS: Record<string, string> = {
    INSS: 'INSS',
    IRRF: 'IRRF',
    MINIMUM_WAGE: 'Salário Mínimo',
    FAMILY_SALARY: 'Salário-Família',
}

interface TableForm {
    type: string
    year: string
    month: string
    data: string
    active: boolean
}

const initialForm: TableForm = {
    type: 'INSS',
    year: new Date().getFullYear().toString(),
    month: '',
    data: '[]',
    active: true,
}

interface RangeRow {
    min: number
    max: number
    rate: number
    deduction?: number
}

function RangeTable({ data }: { data: any }) {
    if (!Array.isArray(data) || data.length === 0) return null

    const hasDeduction = data.some((row: any) => row.deduction != null)

    return (
        <table className="table table-xs mt-2">
            <thead>
                <tr>
                    <th className="text-right">Mínimo</th>
                    <th className="text-right">Máximo</th>
                    <th className="text-right">Alíquota (%)</th>
                    {hasDeduction && <th className="text-right">Dedução</th>}
                </tr>
            </thead>
            <tbody>
                {data.map((row: RangeRow, idx: number) => (
                    <tr key={idx}>
                        <td className="text-right font-mono text-xs">
                            {row.min?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right font-mono text-xs">
                            {row.max != null ? row.max.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '-'}
                        </td>
                        <td className="text-right font-mono text-xs">
                            {row.rate != null ? `${row.rate}%` : '-'}
                        </td>
                        {hasDeduction && (
                            <td className="text-right font-mono text-xs">
                                {row.deduction != null
                                    ? row.deduction.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
                                    : '-'}
                            </td>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

export default function PayrollTablesPage() {
    const { user } = useAuth()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('hr', 'payroll_tables')

    const [tables, setTables] = useState<HrPayrollTable[]>([])
    const [loading, setLoading] = useState(true)
    const [seeding, setSeeding] = useState(false)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<TableForm>(initialForm)
    const [saving, setSaving] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        loadTables()
    }, [])

    const loadTables = async () => {
        setLoading(true)
        try {
            const res = await getPayrollTables()
            if (res.success && res.result) {
                setTables(res.result)
            } else {
                toast.error(res.error || 'Erro ao carregar tabelas')
            }
        } catch {
            toast.error('Erro ao carregar tabelas')
        } finally {
            setLoading(false)
        }
    }

    const handleSeed = async () => {
        setSeeding(true)
        try {
            const res = await seedPayrollTables()
            if (res.success) {
                toast.success('Tabelas 2026 criadas com sucesso')
                loadTables()
            } else {
                toast.error(res.error || 'Erro ao criar tabelas')
            }
        } catch {
            toast.error('Erro ao criar tabelas')
        } finally {
            setSeeding(false)
        }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
    }

    const openEditModal = (table: HrPayrollTable) => {
        setEditingId(table.id)
        setForm({
            type: table.type,
            year: String(table.year),
            month: table.month != null ? String(table.month) : '',
            data: JSON.stringify(table.data, null, 2),
            active: table.active,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const handleSave = async () => {
        if (!form.year) {
            toast.warning('Informe o ano')
            return
        }

        let parsedData: any
        try {
            parsedData = JSON.parse(form.data)
        } catch {
            toast.error('JSON dos dados é inválido')
            return
        }

        setSaving(true)
        try {
            const data: any = {
                type: form.type,
                year: parseInt(form.year, 10),
                month: form.month ? parseInt(form.month, 10) : null,
                data: parsedData,
                active: form.active,
            }

            if (editingId) {
                const res = await updatePayrollTable(editingId, data)
                if (res.success) {
                    toast.success('Tabela atualizada com sucesso')
                    closeModal()
                    loadTables()
                } else {
                    toast.error(res.error || 'Erro ao atualizar tabela')
                }
            } else {
                const res = await createPayrollTable(data)
                if (res.success) {
                    toast.success('Tabela criada com sucesso')
                    closeModal()
                    loadTables()
                } else {
                    toast.error(res.error || 'Erro ao criar tabela')
                }
            }
        } catch {
            toast.error('Erro ao salvar tabela')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deletePayrollTable(deleteId)
            if (res.success) {
                toast.success('Tabela excluída com sucesso')
                setDeleteId(null)
                loadTables()
            } else {
                toast.error(res.error || 'Erro ao excluir tabela')
            }
        } catch {
            toast.error('Erro ao excluir tabela')
        } finally {
            setDeleting(false)
        }
    }

    // Group tables by type
    const groupedTables = tables.reduce<Record<string, HrPayrollTable[]>>((acc, table) => {
        if (!acc[table.type]) acc[table.type] = []
        acc[table.type].push(table)
        return acc
    }, {})

    const isRangeTable = (type: string) => type === 'INSS' || type === 'IRRF'

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando tabelas...</p>
            </div>
        )
    }

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-accent/70 flex items-center justify-center shadow-lg">
                        <MdTableChart className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Tabelas de Cálculo
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {tables.length} {tables.length === 1 ? 'tabela' : 'tabelas'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canCreate && (
                        <button
                            className="btn btn-outline btn-sm gap-2"
                            onClick={handleSeed}
                            disabled={seeding}
                        >
                            {seeding ? (
                                <span className="loading loading-spinner loading-xs"></span>
                            ) : (
                                <MdPlaylistAdd className="text-lg" />
                            )}
                            Seed 2026
                        </button>
                    )}
                    {canCreate && (
                        <button className="btn btn-secondary gap-2" onClick={openCreateModal}>
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">Nova Tabela</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Grouped tables */}
            {tables.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdTableChart className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            Nenhuma tabela cadastrada
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Crie tabelas manualmente ou utilize o seed
                        </p>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    {Object.entries(groupedTables).map(([type, tablesOfType]) => (
                        <div key={type} className="card bg-base-100 border border-base-300/50 shadow-sm">
                            <div className="card-body">
                                <h2 className="card-title text-lg mb-4">
                                    {TYPE_LABELS[type] || type}
                                    <span className="badge badge-ghost badge-sm">{tablesOfType.length}</span>
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {tablesOfType.map(table => (
                                        <div
                                            key={table.id}
                                            className="border border-base-300/50 rounded-lg p-4"
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-semibold">
                                                        {table.year}
                                                        {table.month != null && `/${String(table.month).padStart(2, '0')}`}
                                                    </span>
                                                    {table.active ? (
                                                        <span className="badge badge-success badge-xs">Ativo</span>
                                                    ) : (
                                                        <span className="badge badge-ghost badge-xs">Inativo</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {canUpdate && (
                                                        <button
                                                            onClick={() => openEditModal(table)}
                                                            className="btn btn-ghost btn-xs"
                                                            title="Editar"
                                                        >
                                                            <MdEdit className="text-lg" />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() => setDeleteId(table.id)}
                                                            className="btn btn-ghost btn-xs text-error"
                                                            title="Excluir"
                                                        >
                                                            <MdDelete className="text-lg" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {isRangeTable(type) && Array.isArray(table.data) ? (
                                                <RangeTable data={table.data} />
                                            ) : (
                                                <pre className="text-xs bg-base-200/50 rounded-lg p-3 overflow-x-auto max-h-40">
                                                    {JSON.stringify(table.data, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <h3 className="font-bold text-lg mb-4">
                        {editingId ? 'Editar Tabela' : 'Nova Tabela'}
                    </h3>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.type}
                                onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                            >
                                {Object.entries(TYPE_LABELS).map(([key, label]) => (
                                    <option key={key} value={key}>{label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Ano *</span>
                                </label>
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="2026"
                                    value={form.year}
                                    onChange={e => setForm(prev => ({ ...prev, year: e.target.value }))}
                                />
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Mês (opcional)</span>
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="12"
                                    className="input input-bordered w-full"
                                    placeholder="1-12"
                                    value={form.month}
                                    onChange={e => setForm(prev => ({ ...prev, month: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Dados (JSON) *</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full font-mono text-xs"
                                rows={10}
                                placeholder='[{"min": 0, "max": 1412, "rate": 7.5, "deduction": 0}]'
                                value={form.data}
                                onChange={e => setForm(prev => ({ ...prev, data: e.target.value }))}
                            />
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    Para INSS/IRRF: array com min, max, rate, deduction
                                </span>
                            </label>
                        </div>

                        <div className="form-control">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary toggle-sm"
                                    checked={form.active}
                                    onChange={e => setForm(prev => ({ ...prev, active: e.target.checked }))}
                                />
                                <span className="label-text">Ativo</span>
                            </label>
                        </div>
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
                            {saving && <span className="loading loading-spinner loading-sm"></span>}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Tabela"
                message="Tem certeza de que deseja excluir esta tabela? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
